// Bank floor openings — the front/rear doorway configuration of a bank's floors.
//
// Extracted from setupWizard/steps/StepActions.tsx, which owned these while the
// wizard's action seeder was the only consumer. The scheduled-action editor now
// renders the same openings as a per-floor checkbox grid, so the shape and its
// extractor live in a shared util: a bank whose floors are read one way by the
// seeder and another way by the editor would show an operator a grid that does
// not match the actions the wizard generates.

import formatUuidT from "../flatbuffers/display/uuid";

/**
 * Bank floor record used by the door-aware action seeder and by the scheduled
 * action floor picker. `id` ties back to `LiftFloorInterface.bankFloorId`;
 * `position` is the floor number stamped into action.target_position. front/rear
 * flags come from BankFloorInterface and answer "does ANY lift in the bank have a
 * front/rear door at this floor?".
 */
export interface BankFloorOpening {
  id: string;
  /**
   * The floor's `bank_floors.position` column, VERBATIM.
   *
   * This is an identity, not a rank — but it is no longer the floor number
   * LiftCloud's scheduler dispatches against. `attachFloors`
   * (`internal/scheduler/scheduler.go`) was rewritten to build each bank's
   * floor set from `BankFloor.FloorIndex` instead, because `position` has no
   * database constraint and is 0 for every row in real data: a floor set keyed
   * on it collapsed to {0}, and the scheduler silently dropped every dispatch
   * target whose position was not zero.
   *
   * `position` is still the right value to send, though, because it is still
   * what `validateFloorSpecAgainstBanks`
   * (`controllers/timed-action.controller.go`) validates a saved `floor_spec`
   * against, not `FloorIndex`. A `floor_no`/`floor_index` substituted here can
   * be rejected outright at save time. (Validation and dispatch have keyed on
   * two different `BankFloor` columns since the scheduler rewrite, with
   * nothing reconciling them: a `position` that differs from its floor's
   * `floor_index` now passes validation and is then silently dropped at
   * dispatch instead. That gap belongs to the two LiftCloud endpoints, not to
   * this field.) `Position` also remains the live display-order column
   * `resequenceBankFloorPositions` (`controllers/bank_floor_delete.go`)
   * maintains on floor delete, and it round-trips verbatim through the bank
   * read/update API — it has real readers and writers; it is just no longer
   * the scheduler's.
   *
   * It is therefore still NEVER swapped for `floor_no` or `floor_index`, even
   * when the column is degenerate. `order` below exists for the case that
   * substitution was originally written to solve.
   */
  position: number;
  front: boolean;
  rear: boolean;
  /**
   * Where the floor sits vertically, used ONLY to identify the top and bottom of
   * a bank for the hall-call extremes rule. Normally equal to `position`; it
   * falls back to `floor_no`/`floor_index` when the position column is degenerate
   * (identical across every floor, typically 0 because it was never populated).
   *
   * Kept separate from `position` because the two answer different questions —
   * "which floor is this" versus "how high is it" — and only the first is
   * addressable on the wire. Conflating them is what made a rank substitutable
   * for an identity.
   */
  order: number;
  /**
   * The bank floor's display name ("Lobby", "P2"). OPTIONAL because the action
   * seeder never needed it and its callers do not all supply one; the floor
   * picker falls back to the position when it is absent.
   */
  name?: string;
  /**
   * The floor's raw `floorIndex`/`floor_index`, exposed for callers — the
   * wizard's `bankDoorSignature.ts` is the first — that must key/sort rows by
   * the row's own semantic identity rather than `position` (frozen at create
   * time on some sites, see `position`'s comment above) or `order` (a LOCAL
   * top/bottom ranking, not an identity, and not always equal to floorIndex).
   *
   * OPTIONAL and `undefined` — never `0` or the row's array position — when
   * the row names neither casing. Conflating "absent" with "floor zero" is
   * the exact class of bug `position`'s own history above documents; this
   * field does not repeat it.
   */
  floorIndex?: number;
}

/**
 * Read one property off a value that may not be an object at all. Returns
 * undefined for a null, a primitive or a missing key, so a caller can chain
 * candidate field names without asserting a shape it has not checked.
 */
function readField(source: unknown, key: string): unknown {
  if (typeof source !== "object" || source === null) return undefined;

  return (source as Record<string, unknown>)[key];
}

/**
 * Read the first candidate name a row actually carries, so a caller names a
 * field once instead of chaining `??` across its casings. A key present with
 * an explicit `null` WINS over a later candidate name: "recorded as null" is
 * a value the coercers below decide about, not a reason to fall through.
 */
function readFirstField(source: unknown, ...keys: string[]): unknown {
  const key = keys.find((k) => readField(source, k) !== undefined);

  return key !== undefined ? readField(source, key) : undefined;
}

/**
 * Strip the JSON envelope, if there is one.
 *
 * `GET /banks/{id}` answers in TWO shapes, chosen by the Accept header. A client
 * on `createFlatBufferBaseQuery` sends `Accept: application/flatbuffers` and gets
 * a packed Bank, which the base query unpacks and returns AS the bank. A legacy
 * JSON client gets `{status, data: bank}`. So the bank is sometimes the value
 * itself and sometimes one level down, and reading only `.data` finds nothing on
 * the FlatBuffer path — which is every config_wizard build.
 *
 * `bank_floors` is checked, not just `data`, because an envelope is identified by
 * what it wraps: a Bank never carries a `data` field of its own, but proving that
 * from the shape in hand is better than assuming it.
 */
export function unwrapBankResponse(response: unknown): unknown {
  const inner = readField(response, "data");
  if (inner === undefined || inner === null) return response;

  const looksLikeBank =
    readField(response, "bankFloors") !== undefined ||
    readField(response, "bank_floors") !== undefined;

  return looksLikeBank ? response : inner;
}

/**
 * Coerce a FlatBuffer string field, which arrives as a `string` when the table
 * was built in JS and as raw UTF-8 bytes when it came off the wire.
 * `String(uint8Array)` yields the comma-joined byte values ("76,111,98,98,121"),
 * not the text.
 */
export function coerceFlatBufferText(value: unknown): string | undefined {
  if (typeof value === "string") return value === "" ? undefined : value;
  if (value instanceof Uint8Array) {
    const decoded = new TextDecoder().decode(value);

    return decoded === "" ? undefined : decoded;
  }

  return undefined;
}

/**
 * Coerce a door flag, falling back only when the field is genuinely absent.
 *
 * A boolean is taken as-is. A NUMBER is taken as 0/1, because a serialiser that
 * emits door flags numerically must not have its `0` read as "not recorded" and
 * then flipped to the permissive default — that would report a front opening at
 * every floor of a bank that has none. `null` and `undefined` alone mean "not
 * recorded", where the fallback is right: reading an unset flag as false hides
 * every opening in the bank behind an empty picker.
 */
export function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  return fallback;
}

/**
 * Read a row's door flags — the `frontDoor`/`front_door` and `rearDoor`/
 * `rear_door` pairs that `bank_floors` and `lift_floors` both carry — with
 * the CALLER's fallback for a genuinely-absent flag.
 *
 * The fallback is a PARAMETER, not a constant, because it is the one thing
 * two consumers of this reader may legitimately disagree about: this file's
 * own `extractBankFloorOpenings` below answers "does ANY lift have this
 * opening" and defaults front-open/rear-closed, while `bankDoorSignature.ts`'s
 * `resolveBankFloorDoorFields` must default to whatever the operator set on
 * the bank-level column instead. What the two must NEVER disagree about is
 * WHICH keys name a door and HOW a raw value becomes a boolean — so that half
 * lives here, once, shared by both.
 */
export function readDoorFlags(
  row: unknown,
  fallbackFront: boolean,
  fallbackRear: boolean,
): { front: boolean; rear: boolean } {
  return {
    front: asBool(readFirstField(row, "frontDoor", "front_door"), fallbackFront),
    rear: asBool(readFirstField(row, "rearDoor", "rear_door"), fallbackRear),
  };
}

/**
 * Read a row's floor index under either casing, UNCOERCED — `undefined` when
 * the row names neither. Deliberately left raw: its callers need different
 * answers for an absent value. `extractBankFloorOpenings` below coerces it
 * with `num()` into `NaN`, because `chooseFloorOrderField` explicitly rejects
 * `NaN` to decide a field cannot recover the ordering.
 * `bankDoorSignature.ts`'s raw-floors path instead falls back to the row's
 * ARRAY INDEX. Coercing here would force one answer on both callers; leaving
 * it raw lets each apply its own — `floorIndexOrZero` just below is a third
 * answer, shared because enough callers happened to agree on it, not a
 * replacement for this one.
 */
export function readFloorIndexField(row: unknown): unknown {
  return readFirstField(row, "floorIndex", "floor_index");
}

/**
 * Coerce a row's floor index to a number, answering `0` for a row that names
 * neither casing, names one explicitly `null`, or carries a value that will
 * not coerce.
 *
 * This is the answer three UI readers of this same raw field arrived at
 * independently and then each kept a private copy of — `StepLiftsFloors.tsx`
 * (two floor-sort comparators, plus `populateBankFloorEntries`, which sorts
 * AND writes the value), `TerminalPage.tsx`'s `normalizeFloors`, and
 * `MonitoringBank.tsx`'s. Five call sites, one behaviour, three copies that
 * could only stay in agreement about a casing or about `null` for as long as
 * nobody edited one of them. They are one function now, for the same reason
 * `readDoorFlags` above is one function.
 *
 * It deliberately does NOT match this file's own `numOrAbsent` below, which
 * maps `null` to `NaN` so `extractBankFloorOpenings` can publish "absent" on
 * `BankFloorOpening.floorIndex` and refuse to recover an ordering from a row
 * that has no index. Both answers are correct for their caller: a UI list
 * that must render and sort every row it is handed needs a number for every
 * row, while a public shape that must not invent a phantom floor zero needs
 * to be able to say "no index at all". Do not unify the two.
 *
 * `0` is cheap at most call sites, but PERSISTS at `StepLiftsFloors.tsx`'s
 * `populateBankFloorEntries` — see the comment there before changing this
 * fallback.
 */
export function floorIndexOrZero(row: unknown): number {
  // Number() is already the identity on a value that's already a number
  // (Number(5) === 5, Number(NaN) === NaN, ...), so coercing unconditionally
  // needs no separate typeof branch for the already-a-number case.
  const coerced = Number(readFloorIndexField(row));

  // Number(undefined) is already NaN, and Number(null) is already 0 — the
  // same "absent" answer this function commits to — so Number.isNaN alone
  // covers both without a separate undefined/null check.
  return Number.isNaN(coerced) ? 0 : coerced;
}

/**
 * Coerce a floor index to a number, treating BOTH `undefined` and `null` as
 * "not recorded" (`NaN`) — unlike the generic `num()` above, where
 * `Number(null)` is `0`, a real number. `position`/`floorNo` deliberately
 * keep `num()`'s plain behavior (their own history, see `position`'s comment
 * on `BankFloorOpening`, never needed this distinction); `floorIndex` must
 * make it, for two callers of this exact value: `chooseFloorOrderField`
 * below rejects `NaN` to decide a field cannot recover the ordering, and the
 * public `BankFloorOpening.floorIndex` must never publish a phantom floor
 * zero for a row whose index was never recorded (the same degenerate-column
 * bug class `position`'s comment documents). Using the generic coercer for
 * both would let an explicit `floorIndex: null` recover the ordering as a
 * legitimate "0" while simultaneously reporting "absent" on the public
 * field — two different answers to "does this floor have an index" for the
 * same row.
 */
function numOrAbsent(v: unknown): number {
  if (v === undefined || v === null) return NaN;

  return typeof v === "number" ? v : Number(v);
}

/**
 * Pick which field to use as a floor's vertical position for the top/bottom
 * hall-call exclusion. Returns null to keep the raw `position` (the normal
 * case). Returns "floorNo"/"floorIndex" ONLY when `position` is degenerate —
 * identical across every floor of a multi-floor bank, typically 0 because the
 * column was never populated — AND that field is distinct across the floors,
 * recovering the real order. Without this a bank whose position column is all
 * 0 makes Math.max === Math.min, so eligibleFloorPositions treats every floor
 * as BOTH the top and the bottom and drops floor-based (up/down hall-call)
 * controls from auto-generation.
 */
function chooseFloorOrderField(
  floors: { position: number; floorNo: number; floorIndex: number }[],
): "floorNo" | "floorIndex" | null {
  if (floors.length < 2) return null;
  const positionDegenerate = floors.every((f) => f.position === floors[0].position);
  if (!positionDegenerate) return null;
  const isDistinct = (key: "floorNo" | "floorIndex"): boolean =>
    floors.every((f) => !Number.isNaN(f[key])) &&
    new Set(floors.map((f) => f[key])).size === floors.length;
  if (isDistinct("floorNo")) return "floorNo";
  if (isDistinct("floorIndex")) return "floorIndex";
  return null;
}

/**
 * Pull the bank's per-floor opening list off the GORM-preloaded bank row.
 * Accepts both camelCase (FlatBuffer/API hydrated) and snake_case (legacy
 * JSON) field names. Returns an empty array when bankFloors aren't
 * present, in which case the seeder falls back to a generic floor range.
 */
// The bank row arrives from several shapes that share no declared type
// (FlatBuffer-hydrated, legacy JSON, RTK Query cache), so the parameter is
// `unknown` and every field is read through a guarded accessor rather than
// asserted. This is stricter than the `any` the wizard used before the move:
// there, a typo in a field name was silently `undefined`; here it does not
// compile.
export function extractBankFloorOpenings(bankData: unknown): BankFloorOpening[] {
  const bank = unwrapBankResponse(bankData);
  const raw = readField(bank, "bankFloors") ?? readField(bank, "bank_floors");
  if (!Array.isArray(raw)) return [];
  const num = (v: unknown): number => (typeof v === "number" ? v : Number(v));
  const parsed = raw
    .map((bf) => {
      const read = (...keys: string[]): unknown => readFirstField(bf, ...keys);
      // `id` is a `UuidT` on the FlatBuffer path, not a string. String() on one
      // yields "[object Object]" — non-empty, so it passes the guard below while
      // giving EVERY floor the same id: colliding React keys, and a per-lift door
      // lookup that matches the wrong floor or none.
      const id = formatUuidT(read("id", "ID") as Parameters<typeof formatUuidT>[0]);
      // Capture position AND the two ordering fields separately. A plain
      // `position ?? floorNo ?? floor_no` keeps a present-but-zero position and
      // shadows the distinct floor_no, which is exactly the bug on banks whose
      // position column is 0 for every floor. chooseFloorOrderField picks a
      // usable order below.
      const position = num(read("position", "floorNo", "floor_no"));
      const floorNo = num(read("floorNo", "floor_no"));
      const floorIndex = numOrAbsent(readFloorIndexField(bf));
      if (!id || Number.isNaN(position)) return null;
      const name = coerceFlatBufferText(read("name", "Name"));
      return {
        id,
        position,
        floorNo,
        floorIndex,
        ...readDoorFlags(bf, true, false),
        name,
      };
    })
    .filter((b): b is NonNullable<typeof b> => b !== null);

  const orderField = chooseFloorOrderField(parsed);

  // The recovered field becomes `order`, NOT `position`. It used to overwrite
  // position, which was safe while its only consumer was the seeder's top/bottom
  // exclusion — a purely local ranking. It stopped being safe the moment a floor
  // number reached the wire: the scheduled-action grid serialises these into
  // `floor_spec`, and LiftCloud matches that against the raw `position` column,
  // so a substituted number produces an action that saves, reads as enabled, and
  // silently never fires.
  return parsed.map((f) => {
    // `f.floorIndex` is already NaN-on-absent-or-null (via `numOrAbsent`),
    // so this is just the undefined-when-NaN projection onto the public
    // shape — no second raw-value check needed now that one coercion serves
    // both this and `chooseFloorOrderField` above.
    const floorIndex = Number.isNaN(f.floorIndex) ? undefined : f.floorIndex;

    return {
      id: f.id,
      position: f.position,
      order: orderField ? f[orderField] : f.position,
      front: f.front,
      rear: f.rear,
      ...(f.name !== undefined ? { name: f.name } : {}),
      ...(floorIndex !== undefined ? { floorIndex } : {}),
    };
  });
}

/** One lift's doors at one bank floor, keyed by that floor's id. */
export type LiftDoorMap = Map<string, { front: boolean; rear: boolean }>;

/**
 * Pull each lift's per-floor door configuration off the bank, keyed by lift id.
 *
 * `bank_floors.front_door`/`rear_door` answer "does ANY lift in the bank have
 * this opening at this floor". `lift_floors` answers it per CAR, which is the
 * question a scheduled action targeting specific lifts actually asks: a bank
 * where car 1 has a rear door at the lobby and car 2 does not is ordinary, and
 * the bank-level flag says only that one of them does.
 *
 * A lift with no `lift_floors` rows is absent from the result, which callers read
 * as "fall back to the bank-floor flags" — the same degradation the wizard's
 * seeder has always used for an un-hydrated lift.
 */
export function extractLiftDoorMaps(bankData: unknown): Record<string, LiftDoorMap> {
  const bank = unwrapBankResponse(bankData);
  const lifts = readField(bank, "lifts") ?? readField(bank, "Lifts");
  if (!Array.isArray(lifts)) return {};

  const out: Record<string, LiftDoorMap> = {};

  lifts.forEach((lift) => {
    const liftId = formatUuidT(
      (readField(lift, "id") ?? readField(lift, "ID")) as Parameters<typeof formatUuidT>[0],
    );
    if (liftId === "") return;

    const floors = readField(lift, "liftFloors") ?? readField(lift, "lift_floors");
    if (!Array.isArray(floors) || floors.length === 0) return;

    const doors: LiftDoorMap = new Map();
    floors.forEach((liftFloor) => {
      // Keyed by bank_floor_id, and formatted rather than String()-ed: these are
      // Uuids on the FlatBuffer path, and String() renders every one of them as
      // "[object Object]" — one key for the whole map.
      const bankFloorId = formatUuidT(
        (readField(liftFloor, "bankFloorId") ??
          readField(liftFloor, "bank_floor_id")) as Parameters<typeof formatUuidT>[0],
      );
      if (bankFloorId === "") return;

      // Same shared reader `extractBankFloorOpenings` above uses, and the
      // same front-open/rear-closed defaults — so a casing, a numeric-0 flag
      // or a present-`null` cannot mean one thing for a bank floor and
      // another for a lift floor. This used to read `??` (a present-but-null
      // key falls through to the next candidate) where readDoorFlags reads
      // `!== undefined` (a present-but-null key wins outright); the two are
      // equivalent on every row this function actually receives — a
      // FlatBuffer-unpacked LiftFloorT (camelCase only, a scalar bool is
      // never null) or legacy JSON (snake_case only) — see this file's own
      // tests, which pin both the round-tripped wire shape and the
      // plain-object null-precedence case.
      doors.set(bankFloorId, readDoorFlags(liftFloor, true, false));
    });

    if (doors.size > 0) out[liftId] = doors;
  });

  return out;
}

/**
 * Resolve a bank's floors against the doors of the lifts an action targets.
 *
 * An opening is offered when ANY targeted lift has it at that floor. Union, not
 * intersection: the dispatcher emits one command per (lift, floor) pair and drops
 * what a car cannot do, so hiding a floor because ONE of three cars lacks a rear
 * door would make it unreachable for the two that have one.
 *
 * `liftIds` empty means the action is bank-wide, so every lift counts — which is
 * exactly what the bank-level flags already summarise, and they are used as-is.
 * A targeted lift with no per-floor rows contributes the bank-level flags too,
 * rather than nothing.
 */
export function resolveOpeningsForLifts(
  floors: BankFloorOpening[],
  liftIds: string[],
  liftDoors: Record<string, LiftDoorMap>,
): BankFloorOpening[] {
  if (liftIds.length === 0) return floors;

  return floors.map((floor) => {
    let front = false;
    let rear = false;

    liftIds.forEach((liftId) => {
      const doors = liftDoors[liftId]?.get(floor.id) ?? { front: floor.front, rear: floor.rear };
      front = front || doors.front;
      rear = rear || doors.rear;
    });

    return { ...floor, front, rear };
  });
}
